import { Capacitor } from '@capacitor/core';

export type GeolocationErrorCode =
  | 'unsupported'
  | 'denied'
  | 'unavailable'
  | 'timeout'
  | 'unknown';

export class GeolocationRequestError extends Error {
  readonly code: GeolocationErrorCode;

  constructor(code: GeolocationErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'GeolocationRequestError';
    this.code = code;
  }
}

const DEFAULT_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15_000,
  maximumAge: 60_000,
};

const FALLBACK_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 20_000,
  maximumAge: 120_000,
};

type NativeGeolocationPlugin = {
  checkPermissions: () => Promise<{
    location?: string;
    coarseLocation?: string;
  }>;
  requestPermissions: () => Promise<{
    location?: string;
    coarseLocation?: string;
  }>;
  getCurrentPosition: (options?: {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
  }) => Promise<{
    coords: {
      latitude: number;
      longitude: number;
      accuracy: number;
      altitude: number | null;
      altitudeAccuracy?: number | null;
      heading: number | null;
      speed: number | null;
    };
  }>;
};

/** Évite les appels natifs concurrents (dialog permissions / GPS) qui plantent par intermittence. */
let nativeInFlight: Promise<GeolocationCoordinates> | null = null;

function shouldRetryWithLowAccuracy(error: unknown): boolean {
  return (
    error instanceof GeolocationRequestError &&
    (error.code === 'timeout' || error.code === 'unavailable' || error.code === 'unknown')
  );
}

function isUnimplementedPluginError(error: unknown): boolean {
  const code = String((error as { code?: string } | null)?.code ?? '').toUpperCase();
  const message = String((error as { message?: string } | null)?.message ?? '').toLowerCase();
  return (
    code === 'UNIMPLEMENTED' ||
    message.includes('not implemented') ||
    message.includes('plugin is not implemented')
  );
}

function isFiniteCoord(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function assertValidCoordinates(
  coords: GeolocationCoordinates,
): GeolocationCoordinates {
  if (!isFiniteCoord(coords.latitude) || !isFiniteCoord(coords.longitude)) {
    throw new GeolocationRequestError('unavailable', 'invalid coordinates');
  }
  if (Math.abs(coords.latitude) > 90 || Math.abs(coords.longitude) > 180) {
    throw new GeolocationRequestError('unavailable', 'out of range coordinates');
  }
  return coords;
}

export async function getCurrentCoordinates(
  options?: PositionOptions,
): Promise<GeolocationCoordinates> {
  const resolved = { ...DEFAULT_OPTIONS, ...options };

  const run = async (): Promise<GeolocationCoordinates> => {
    try {
      if (Capacitor.isNativePlatform()) {
        return await getNativeCoordinates(resolved);
      }
      return await getBrowserCoordinates(resolved);
    } catch (error) {
      // Simulateur / GPS lent : 2ᵉ tentative en précision basse.
      if (shouldRetryWithLowAccuracy(error) && resolved.enableHighAccuracy !== false) {
        const fallback = { ...FALLBACK_OPTIONS, ...options, enableHighAccuracy: false };
        if (Capacitor.isNativePlatform()) {
          return getNativeCoordinates(fallback, { skipPermissionRequest: true });
        }
        return getBrowserCoordinates(fallback);
      }
      throw error;
    }
  };

  if (!Capacitor.isNativePlatform()) {
    return run();
  }

  if (nativeInFlight) {
    return nativeInFlight;
  }

  nativeInFlight = run().finally(() => {
    nativeInFlight = null;
  });
  return nativeInFlight;
}

async function loadNativeGeolocationPlugin(): Promise<NativeGeolocationPlugin> {
  try {
    // Ne jamais retourner le proxy Capacitor depuis un .then() : c’est un thenable
    // (`Plugin.then()` → "not implemented") — même classe d’erreurs Badge/Push en Sentry.
    const mod = await import('@capacitor/geolocation');
    const plugin = mod.Geolocation as NativeGeolocationPlugin | undefined;
    if (
      !plugin ||
      typeof plugin.checkPermissions !== 'function' ||
      typeof plugin.requestPermissions !== 'function' ||
      typeof plugin.getCurrentPosition !== 'function'
    ) {
      throw new GeolocationRequestError('unsupported');
    }
    return plugin;
  } catch (error) {
    if (error instanceof GeolocationRequestError) throw error;
    if (isUnimplementedPluginError(error)) {
      throw new GeolocationRequestError('unsupported');
    }
    throw new GeolocationRequestError('unsupported', String((error as Error)?.message ?? error));
  }
}

function isLocationDenied(permission: {
  location?: string;
  coarseLocation?: string;
}): boolean {
  const fine = permission.location;
  const coarse = permission.coarseLocation;
  // Sur iOS coarseLocation peut être absent : ne juger que `location`.
  if (fine === 'denied' && (coarse === undefined || coarse === 'denied')) {
    return true;
  }
  return fine === 'denied' && coarse === 'denied';
}

function isLocationGranted(permission: {
  location?: string;
  coarseLocation?: string;
}): boolean {
  return permission.location === 'granted' || permission.coarseLocation === 'granted';
}

async function ensureNativeLocationPermission(
  Geolocation: NativeGeolocationPlugin,
  options?: { skipPermissionRequest?: boolean },
): Promise<void> {
  let permission;
  try {
    permission = await Geolocation.checkPermissions();
  } catch (error) {
    if (isUnimplementedPluginError(error)) {
      throw new GeolocationRequestError('unsupported');
    }
    // Anciennes builds : checkPermissions peut manquer → tenter request directement.
    permission = null;
  }

  if (permission && isLocationGranted(permission)) {
    return;
  }
  if (permission && isLocationDenied(permission)) {
    throw new GeolocationRequestError('denied');
  }
  if (options?.skipPermissionRequest) {
    // Retry GPS : ne pas rouvrir le dialog. Denied strict seulement.
    if (permission && isLocationDenied(permission)) {
      throw new GeolocationRequestError('denied');
    }
    return;
  }

  try {
    permission = await Geolocation.requestPermissions();
  } catch (error) {
    if (error instanceof GeolocationRequestError) throw error;
    if (isUnimplementedPluginError(error)) {
      throw new GeolocationRequestError('unsupported');
    }
    throw mapNativeGeolocationError(error);
  }

  if (isLocationDenied(permission)) {
    throw new GeolocationRequestError('denied');
  }
  if (!isLocationGranted(permission) && permission.location === 'prompt') {
    // Dialog fermé sans choix clair.
    throw new GeolocationRequestError('denied');
  }
}

async function getNativeCoordinates(
  options: PositionOptions,
  extras?: { skipPermissionRequest?: boolean },
): Promise<GeolocationCoordinates> {
  try {
    const Geolocation = await loadNativeGeolocationPlugin();
    await ensureNativeLocationPermission(Geolocation, extras);

    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: options.enableHighAccuracy,
      timeout: options.timeout,
      maximumAge: options.maximumAge,
    });

    if (!position?.coords) {
      throw new GeolocationRequestError('unavailable');
    }

    return assertValidCoordinates(toDomCoordinates(position.coords));
  } catch (error) {
    if (error instanceof GeolocationRequestError) throw error;
    if (isUnimplementedPluginError(error)) {
      throw new GeolocationRequestError('unsupported');
    }
    throw mapNativeGeolocationError(error);
  }
}

function getBrowserCoordinates(
  options: PositionOptions,
): Promise<GeolocationCoordinates> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new GeolocationRequestError('unsupported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          resolve(assertValidCoordinates(position.coords));
        } catch (error) {
          reject(error);
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(new GeolocationRequestError('denied'));
          return;
        }
        if (error.code === error.POSITION_UNAVAILABLE) {
          reject(new GeolocationRequestError('unavailable'));
          return;
        }
        if (error.code === error.TIMEOUT) {
          reject(new GeolocationRequestError('timeout'));
          return;
        }
        reject(new GeolocationRequestError('unknown'));
      },
      options,
    );
  });
}

function toDomCoordinates(coords: {
  latitude: number;
  longitude: number;
  accuracy: number;
  altitude: number | null;
  altitudeAccuracy?: number | null;
  heading: number | null;
  speed: number | null;
}): GeolocationCoordinates {
  if (!Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude)) {
    throw new GeolocationRequestError('unavailable');
  }
  return {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy: coords.accuracy,
    altitude: coords.altitude,
    altitudeAccuracy: coords.altitudeAccuracy ?? null,
    heading: coords.heading,
    speed: coords.speed,
  } as GeolocationCoordinates;
}

function mapNativeGeolocationError(error: unknown): GeolocationRequestError {
  const code = String((error as { code?: string } | null)?.code ?? '');
  const message = String((error as { message?: string } | null)?.message ?? '').toLowerCase();

  if (isUnimplementedPluginError(error)) {
    return new GeolocationRequestError('unsupported');
  }
  if (
    code === 'OS-PLUG-GLOC-0003' ||
    code === 'OS-PLUG-GLOC-0008' ||
    message.includes('denied') ||
    message.includes('restricted') ||
    message.includes('permission')
  ) {
    return new GeolocationRequestError('denied');
  }
  if (code === 'OS-PLUG-GLOC-0010' || message.includes('timeout')) {
    return new GeolocationRequestError('timeout');
  }
  if (
    code === 'OS-PLUG-GLOC-0007' ||
    code === 'OS-PLUG-GLOC-0009' ||
    message.includes('not enabled') ||
    message.includes('unavailable') ||
    message.includes('disabled')
  ) {
    return new GeolocationRequestError('unavailable');
  }
  return new GeolocationRequestError('unknown');
}

/** Ville / code postal lisible à partir de coordonnées GPS (Nominatim). */
export async function reverseGeocodeCoords(
  lat: number,
  lon: number,
  language = 'fr',
): Promise<string | null> {
  if (!isFiniteCoord(lat) || !isFiniteCoord(lon)) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lon))}&format=json&zoom=14`;
    const resp = await fetch(url, {
      headers: { 'Accept-Language': language },
    });
    if (!resp.ok) return null;

    const data = (await resp.json()) as {
      display_name?: string;
      address?: {
        city?: string;
        town?: string;
        village?: string;
        municipality?: string;
        county?: string;
        postcode?: string;
      };
    };

    const address = data.address;
    if (!address) return data.display_name?.split(',')[0]?.trim() ?? null;

    const city =
      address.city ||
      address.town ||
      address.village ||
      address.municipality ||
      address.county;
    const postcode = address.postcode;

    if (city && postcode) return `${city} (${postcode})`;
    if (city) return city;
    return data.display_name?.split(',')[0]?.trim() ?? null;
  } catch {
    return null;
  }
}

/** Reset mutex (tests). */
export function _resetGeolocationInFlightForTests(): void {
  nativeInFlight = null;
}
